import React, { useState, useEffect, useRef } from 'react';
import { MdCheckCircle, MdStar } from 'react-icons/md';
import { useToast } from '../components/ToastProvider';
import './Subscription.css';

const plans = [
  {
    name: 'Basic',
    price: 19,
    desc: 'Perfect for single-store owners getting started with loyalty.',
    features: ['1 Store', 'Basic loyalty setup', 'Customer management', 'Basic reports'],
    popular: false,
  },
  {
    name: 'Pro',
    price: 49,
    desc: 'For growing businesses managing multiple stores and promotions.',
    features: ['Up to 5 Stores', 'Offers & promotions', 'Advanced reports', 'Customer wallet tracking', 'Priority email support'],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 99,
    desc: 'Full-scale solution for large networks with advanced analytics.',
    features: ['Unlimited stores', 'Commission setup', 'Priority support', 'Advanced analytics', 'Custom integrations', 'Dedicated account manager'],
    popular: false,
  },
];

export default function Subscription() {
  const [yearly, setYearly] = useState(false);
  const toast = useToast();
  const ref = useRef(null);

  useEffect(() => {
    const els = ref.current?.querySelectorAll('.reveal');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('revealed'); observer.unobserve(e.target); } }),
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="subscription" id="subscription" ref={ref}>
      <div className="sub__bg-glow" />
      <div className="container text-center">
        <span className="section-label sub-label reveal">Pricing</span>
        <h2 className="section-title reveal" style={{ color: '#fff', transitionDelay: '0.05s' }}>
          Simple, Transparent Pricing
        </h2>
        <p className="section-subtitle reveal" style={{ color: 'rgba(255,255,255,0.6)', transitionDelay: '0.1s' }}>
          Choose a plan that fits your business. No hidden fees. Cancel anytime.
        </p>

        <div className="subscription__toggle reveal" style={{ transitionDelay: '0.15s' }}>
          <span className={!yearly ? 'active' : ''}>Monthly</span>
          <button
            className={`toggle-btn${yearly ? ' toggle-btn--on' : ''}`}
            onClick={() => setYearly(!yearly)}
            aria-label="Toggle yearly billing"
          >
            <span className="toggle-knob" />
          </button>
          <span className={yearly ? 'active' : ''}>
            Yearly <span className="save-badge">Save 20%</span>
          </span>
        </div>

        <div className="subscription__grid">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`plan-card reveal${plan.popular ? ' plan-card--popular' : ''}`}
              style={{ transitionDelay: `${0.15 + i * 0.1}s` }}
            >
              {plan.popular && (
                <div className="plan-card__popular-badge">
                  <MdStar /> Most Popular
                </div>
              )}
              <div className="plan-card__header">
                <h3>{plan.name}</h3>
                <div className="plan-card__price">
                  <span className="plan-card__currency">$</span>
                  <span className="plan-card__amount">
                    {yearly ? Math.round(plan.price * 12 * 0.8) : plan.price}
                  </span>
                  <span className="plan-card__period">/{yearly ? 'yr' : 'mo'}</span>
                </div>
                <p>{plan.desc}</p>
              </div>
              <ul className="plan-card__features">
                {plan.features.map((f) => (
                  <li key={f}>
                    <MdCheckCircle style={{ color: plan.popular ? '#fff' : 'var(--success)' }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                className={`plan-card__btn${plan.popular ? ' plan-card__btn--white' : ''}`}
                onClick={() => toast({ message: `${plan.name} Plan selected! Redirecting to sign up...`, type: 'info' })}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
